import React, { memo, useCallback, useMemo } from "react";
import Form from "react-bootstrap/Form";
import dayjs from "dayjs";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    LineChart,
    Line,
    AreaChart,
    Area,
    ComposedChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";

const INCOME = "#28a745";
const EXPENSE = "#dc3545";
/** Лінії «плавні»: кольори серій узгоджені зі стовпчиками. */
const LINE_INCOME_STROKE = INCOME;
const LINE_EXPENSE_STROKE = EXPENSE;

const AREA_EXPENSE_STROKE = "#818cf8";
const AREA_INCOME_STROKE = "#22c55e";

const GRADIENT_INCOME = "txDrillIncomeFill";
const GRADIENT_EXPENSE = "txDrillExpenseFill";

export const TX_DRILL_CHART_KINDS = [
    { id: "groupedBar", label: "Стовпці (поруч)" },
    { id: "line", label: "Лінії (плавні)" },
    { id: "area", label: "Область (градієнт)" },
    { id: "composed", label: "Стовпці + лінія" },
];

export const TX_DRILL_CHART_STORAGE_KEY = "txDrillChartKind";

/** Мінімізує затримку hover у Recharts + вимикає a11y-шар (менше роботи на події клавіатури/focus). */
const CHART_MOUSE_MOVE_INTERVAL_MS = 0;

const chartMouseProps = {
    throttleDelay: CHART_MOUSE_MOVE_INTERVAL_MS,
    accessibilityLayer: false,
};

const tooltipWrapperNoTransition = Object.freeze({ transition: "none" });

function fmtAxisMoney(v) {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1000) return `${Math.round(v / 1000)}k`;
    return `${Math.round(v)}`;
}

function tooltipMoney(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    return `${n.toLocaleString("uk-UA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} грн`;
}

function extractPayload(evt) {
    if (!evt) return null;
    const p = evt.payload ?? evt;
    return p?.date ? p : null;
}

/**
 * Повний календарний місяць на осях X (день) / Y (сума, грн).
 * Тип діаграми — як у прикладах на сайті Recharts (Bar / Line / Area / Composed).
 */
function MonthTransactionsChart({ data, chartKind, onChartKindChange, onSegmentClick }) {
    const tooltipFormatter = useCallback(
        (value, name) => [tooltipMoney(value), name],
        []
    );

    const tooltipLabelFormatter = useCallback((_label, items) => {
        const row = items?.[0]?.payload;
        return row ? dayjs(row.date).format("D MMMM") : "";
    }, []);

    const xTicks = useMemo(() => {
        const n = data.length;
        if (!n) return [];
        if (n <= 18) return data.map(d => d.dayIndex);
        const target = 14;
        const step = Math.max(1, Math.ceil(n / target));
        const ticks = [];
        for (let i = 1; i <= n; i += step) ticks.push(i);
        if (ticks[ticks.length - 1] !== n) ticks.push(n);
        return ticks;
    }, [data]);

    const incomeDot = props => {
        const { cx, cy, payload } = props;
        if (cx == null || cy == null || !payload?.date) return null;
        return (
            <circle
                cx={cx}
                cy={cy}
                r={4}
                fill={INCOME}
                stroke="#fff"
                strokeWidth={1}
                style={{ cursor: "pointer" }}
                onClick={e => {
                    e.stopPropagation();
                    onSegmentClick(payload, "income");
                }}
            />
        );
    };

    const expenseDot = props => {
        const { cx, cy, payload } = props;
        if (cx == null || cy == null || !payload?.date) return null;
        return (
            <circle
                cx={cx}
                cy={cy}
                r={4}
                fill={EXPENSE}
                stroke="#fff"
                strokeWidth={1}
                style={{ cursor: "pointer" }}
                onClick={e => {
                    e.stopPropagation();
                    onSegmentClick(payload, "expense");
                }}
            />
        );
    };

    const hollowLineDot =
        (direction, strokeColor) =>
            props => {
                const { cx, cy, payload } = props;
                if (cx == null || cy == null || !payload?.date) return null;
                return (
                    <circle
                        cx={cx}
                        cy={cy}
                        r={5}
                        fill="var(--bs-body-bg)"
                        stroke={strokeColor}
                        strokeWidth={2}
                        style={{ cursor: "pointer" }}
                        onClick={e => {
                            e.stopPropagation();
                            onSegmentClick(payload, direction);
                        }}
                    />
                );
            };

    const hitDot =
        direction =>
            props => {
                const { cx, cy, payload } = props;
                if (cx == null || cy == null || !payload?.date) return null;
                return (
                    <circle
                        cx={cx}
                        cy={cy}
                        r={10}
                        fill="transparent"
                        stroke="transparent"
                        style={{ cursor: "pointer" }}
                        onClick={e => {
                            e.stopPropagation();
                            onSegmentClick(payload, direction);
                        }}
                    />
                );
            };

    const barClick =
        direction =>
            evt => {
                const p = extractPayload(evt);
                if (p) onSegmentClick(p, direction);
            };

    /* Місце під легенду зверху + підписи осей без накладання на тіки / легенду. */
    const margins = { top: 44, right: 18, left: 58, bottom: 52 };

    const tickStyle = { fontSize: 11, fill: "var(--bs-secondary-color)" };
    const axisNameStyle = {
        fill: "var(--bs-body-color)",
        fontWeight: 600,
        fontSize: 12,
    };

    const legendEl = (
        <Legend
            verticalAlign="top"
            align="center"
            wrapperStyle={{ top: 2, left: 0, width: "100%", paddingBottom: 6 }}
        />
    );

    const axesTooltipLegendBlock = (
        <>
            <XAxis
                dataKey="dayIndex"
                ticks={xTicks}
                tick={tickStyle}
                label={{
                    value: "День місяця",
                    position: "insideBottom",
                    offset: 0,
                    dy: 34,
                    style: axisNameStyle,
                }}
            />
            <YAxis
                tickFormatter={fmtAxisMoney}
                width={54}
                tick={tickStyle}
                label={{
                    value: "грн",
                    angle: -90,
                    position: "insideLeft",
                    dx: -50,
                    style: axisNameStyle,
                }}
            />
            <Tooltip
                isAnimationActive={false}
                animationDuration={0}
                formatter={tooltipFormatter}
                labelFormatter={tooltipLabelFormatter}
                wrapperStyle={tooltipWrapperNoTransition}
            />
            {legendEl}
        </>
    );

    const gridAxesLegend = (
        <>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            {axesTooltipLegendBlock}
        </>
    );

    let chartInner;
    switch (chartKind) {
        case "line":
            chartInner = (
                <LineChart {...chartMouseProps} data={data} margin={margins}>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="currentColor"
                        strokeOpacity={0.22}
                    />
                    {axesTooltipLegendBlock}
                    <Line
                        type="monotone"
                        dataKey="income"
                        name="Доходи"
                        stroke={LINE_INCOME_STROKE}
                        strokeWidth={2}
                        dot={hollowLineDot("income", LINE_INCOME_STROKE)}
                        activeDot={{
                            r: 7,
                            strokeWidth: 2,
                            fill: "var(--bs-body-bg)",
                            stroke: LINE_INCOME_STROKE,
                        }}
                    />
                    <Line
                        type="monotone"
                        dataKey="expense"
                        name="Витрати"
                        stroke={LINE_EXPENSE_STROKE}
                        strokeWidth={2}
                        dot={hollowLineDot("expense", LINE_EXPENSE_STROKE)}
                        activeDot={{
                            r: 7,
                            strokeWidth: 2,
                            fill: "var(--bs-body-bg)",
                            stroke: LINE_EXPENSE_STROKE,
                        }}
                    />
                </LineChart>
            );
            break;
        case "area":
            chartInner = (
                <AreaChart {...chartMouseProps} data={data} margin={margins}>
                    <defs>
                        <linearGradient id={GRADIENT_INCOME} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={AREA_INCOME_STROKE} stopOpacity={0.95} />
                            <stop offset="100%" stopColor={AREA_INCOME_STROKE} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id={GRADIENT_EXPENSE} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={AREA_EXPENSE_STROKE} stopOpacity={0.92} />
                            <stop offset="100%" stopColor={AREA_EXPENSE_STROKE} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="currentColor"
                        strokeOpacity={0.14}
                    />
                    {axesTooltipLegendBlock}
                    {/* Спочатку витрати (нижній шар), поверх доходи — як у типових двосерійних area */}
                    <Area
                        type="monotone"
                        dataKey="expense"
                        name="Витрати"
                        stroke={AREA_EXPENSE_STROKE}
                        strokeWidth={2}
                        fill={`url(#${GRADIENT_EXPENSE})`}
                        dot={hitDot("expense")}
                        activeDot={{ r: 6, strokeWidth: 0 }}
                    />
                    <Area
                        type="monotone"
                        dataKey="income"
                        name="Доходи"
                        stroke={AREA_INCOME_STROKE}
                        strokeWidth={2}
                        fill={`url(#${GRADIENT_INCOME})`}
                        dot={hitDot("income")}
                        activeDot={{ r: 6, strokeWidth: 0 }}
                    />
                </AreaChart>
            );
            break;
        case "composed":
            chartInner = (
                <ComposedChart {...chartMouseProps} data={data} margin={margins}>
                    {gridAxesLegend}
                    <Bar
                        dataKey="expense"
                        name="Витрати"
                        fill={EXPENSE}
                        onClick={barClick("expense")}
                        cursor="pointer"
                    />
                    <Line
                        type="monotone"
                        dataKey="income"
                        name="Доходи"
                        stroke={INCOME}
                        strokeWidth={2}
                        dot={incomeDot}
                        activeDot={{ r: 6 }}
                    />
                </ComposedChart>
            );
            break;
        default:
            chartInner = (
                <BarChart {...chartMouseProps} data={data} margin={margins}>
                    {gridAxesLegend}
                    <Bar
                        dataKey="income"
                        name="Доходи"
                        fill={INCOME}
                        onClick={barClick("income")}
                        cursor="pointer"
                    />
                    <Bar
                        dataKey="expense"
                        name="Витрати"
                        fill={EXPENSE}
                        onClick={barClick("expense")}
                        cursor="pointer"
                    />
                </BarChart>
            );
    }

    return (
        <div
            className={`tx-month-chart${chartKind === "area" ? " tx-month-chart--area-docs" : ""}${chartKind === "line" ? " tx-month-chart--line-ref" : ""}`}
        >
            <div className="mb-2">
                <Form.Select
                    size="sm"
                    className="tx-month-chart-kind"
                    style={{ maxWidth: 280 }}
                    value={chartKind}
                    onChange={e => onChartKindChange(e.target.value)}
                    aria-label="Тип графіка"
                >
                    {TX_DRILL_CHART_KINDS.map(k => (
                        <option key={k.id} value={k.id}>
                            {k.label}
                        </option>
                    ))}
                </Form.Select>
            </div>
            <div className="tx-month-chart-canvas">
                <ResponsiveContainer width="100%" height="100%">
                    {chartInner}
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default memo(MonthTransactionsChart);
